import { TYPE_COLORS } from './constants';

export const sx = {
  page: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    background: '#ffffff',
    minHeight: '100vh',
  },

  stickyNav: {
    position: 'sticky', top: 0, zIndex: 1040, background: '#fff',
    borderBottom: '1px solid #eee', padding: '10px 0', backdropFilter: 'blur(12px)',
  },

  // Airbnb-style hero — clean white canvas with a soft violet wash
  // at the top edge to anchor the brand without dominating the page.
  // gradStart / gradEnd are accepted for API compat but no longer drive
  // a full-bleed gradient; we use a faint radial accent instead.
  // eslint-disable-next-line no-unused-vars
  heroWrap: (gradStart, gradEnd) => ({
    background: '#ffffff',
    color: '#0b1024',
    padding: 'clamp(40px, 7vw, 72px) 0 clamp(32px, 6vw, 56px)',
    position: 'relative', overflow: 'hidden',
    textAlign: 'center',
  }),
  heroOverlay: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at 50% -20%, rgba(124, 58, 237, 0.10) 0%, transparent 55%)',
    pointerEvents: 'none',
  },
  heroGrid: {
    position: 'absolute', inset: 0,
    backgroundImage: 'radial-gradient(rgba(124, 58, 237, 0.06) 1px, transparent 1px)',
    backgroundSize: '24px 24px',
    maskImage: 'radial-gradient(ellipse at center top, #000 20%, transparent 70%)',
    WebkitMaskImage: 'radial-gradient(ellipse at center top, #000 20%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroEyebrow: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '6px 14px', borderRadius: 999,
    background: 'rgba(124, 58, 237, 0.08)',
    border: '1px solid rgba(124, 58, 237, 0.18)',
    color: '#5B21B6', fontSize: '0.74rem', fontWeight: 700,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    marginBottom: 18,
  },
  heroTitle: {
    fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', fontWeight: 800,
    letterSpacing: '-0.025em', lineHeight: 1.12, marginBottom: 14,
    color: '#0b1024',
    maxWidth: 880, marginLeft: 'auto', marginRight: 'auto',
  },
  heroSub: {
    fontSize: 'clamp(0.98rem, 1.5vw, 1.12rem)',
    color: '#5a6478',
    fontWeight: 400, marginBottom: 28,
    maxWidth: 620, marginLeft: 'auto', marginRight: 'auto',
    lineHeight: 1.55,
  },
  // Airbnb-style rounded pill: pure white, soft shadow that lifts on
  // hover, divider lines between fields. Maxes at 850px so it stays
  // a comfortable single line on desktop.
  searchBar: {
    background: '#fff', borderRadius: 999, padding: '6px 6px 6px 8px',
    display: 'flex', alignItems: 'center', gap: 0,
    boxShadow: '0 6px 20px rgba(15, 23, 42, 0.10), 0 1px 3px rgba(15, 23, 42, 0.06)',
    border: '1px solid #e5e7eb',
    flexWrap: 'wrap', maxWidth: 850, margin: '0 auto',
  },
  searchInput: {
    border: 'none', outline: 'none', fontSize: '0.95rem', flex: 1,
    minWidth: 140, padding: '14px 18px', color: '#1a1a1a', background: 'transparent',
    textAlign: 'left',
  },
  searchDivider: { width: 1, height: 32, background: '#e5e7eb', flexShrink: 0 },
  // Round, icon-style submit button at the right edge of the pill.
  searchBtn: (color) => ({
    background: color, color: '#fff', border: 'none', borderRadius: 999,
    padding: '14px 24px', fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
    transition: 'opacity .2s, transform .15s',
    boxShadow: '0 4px 14px rgba(124, 58, 237, 0.35)',
    margin: '0 0 0 4px',
  }),
  searchTrust: {
    marginTop: 18, color: '#5a6478',
    fontSize: '0.82rem', fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    justifyContent: 'center',
  },
  searchTrustItem: {
    display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569',
  },
  statsRow: {
    display: 'flex', gap: 0, marginTop: 36, flexWrap: 'wrap',
    justifyContent: 'center', alignItems: 'stretch',
    background: '#faf7ff',
    border: '1px solid #ede9fe',
    borderRadius: 16, overflow: 'hidden',
    maxWidth: 760, marginLeft: 'auto', marginRight: 'auto',
  },
  statItem: {
    flex: 1, minWidth: 140, padding: '16px 18px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    borderLeft: '1px solid #ede9fe',
    color: '#0b1024',
  },
  statBold: { fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.1, color: '#5B21B6' },
  statLabel: {
    fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.04em',
    textTransform: 'uppercase', color: '#64748b',
  },

  section: (bg = '#ffffff') => ({ background: bg, padding: 'clamp(48px, 8vw, 88px) 0' }),
  // Shared section header — centered, navy headline, slate subtitle.
  // Same visual rhythm as HowItWorks / IndustryUseCases / Pricing so
  // every light-background section reads as part of one design system.
  sectionTitle: {
    fontSize: 'clamp(1.6rem, 2.4vw, 2.2rem)', fontWeight: 800,
    letterSpacing: '-0.015em', marginBottom: 12, color: '#0b1024',
    lineHeight: 1.2, textAlign: 'center',
    maxWidth: 820, marginLeft: 'auto', marginRight: 'auto',
  },
  sectionSub: {
    color: '#5a6478', fontSize: '1rem', lineHeight: 1.6,
    marginBottom: 40, textAlign: 'center',
    maxWidth: 680, marginLeft: 'auto', marginRight: 'auto',
  },

  catCard: (isActive) => ({
    flex: '0 0 auto', width: 150, textAlign: 'center', padding: '24px 16px',
    borderRadius: 14, background: isActive ? '#ede9fe' : '#faf7ff',
    border: isActive ? '2px solid #7C3AED' : '1.5px solid #ede9fe',
    cursor: 'pointer', transition: 'all .2s', userSelect: 'none',
  }),
  catIcon: { fontSize: '2rem', marginBottom: 8, display: 'block' },
  catLabel: { fontWeight: 600, fontSize: '0.88rem', color: '#333', marginBottom: 4 },
  catCount: { fontSize: '0.78rem', color: '#888' },

  featCard: {
    borderRadius: 14, overflow: 'hidden', background: '#ffffff', border: '1px solid #ede9fe',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)', transition: 'box-shadow .25s, transform .25s',
    cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column',
  },
  featImg: (gradient) => ({
    height: 180, background: gradient, display: 'flex',
    alignItems: 'flex-end', justifyContent: 'space-between', padding: 14, position: 'relative',
  }),
  featBody: { padding: '16px 18px 18px', flex: 1, display: 'flex', flexDirection: 'column' },
  featName: { fontWeight: 700, fontSize: '1rem', color: '#1a1a1a', marginBottom: 4 },
  featLoc: { color: '#888', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10 },
  featRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  featPrice: { fontWeight: 700, fontSize: '1.15rem' },
  featPriceUnit: { fontWeight: 400, fontSize: '0.78rem', color: '#888' },
  featAmenities: { fontSize: '0.78rem', color: '#888', marginBottom: 12 },
  featActions: {
    display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #f0f0f0',
  },

  typeBadge: (type) => {
    const c = TYPE_COLORS[type] || TYPE_COLORS.DRY;
    return {
      background: c.bg, color: c.text, fontWeight: 600, fontSize: '0.72rem',
      padding: '3px 10px', borderRadius: 6, textTransform: 'uppercase', letterSpacing: '0.03em',
    };
  },

  hiwCard: { textAlign: 'center', padding: '32px 24px' },
  hiwIconWrap: (color) => ({
    width: 72, height: 72, borderRadius: '50%', background: `${color}12`, color: color,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18,
  }),
  hiwStep: {
    fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.1em', marginBottom: 6, color: '#999',
  },
  hiwTitle: { fontWeight: 700, fontSize: '1.1rem', marginBottom: 8, color: '#1a1a1a' },
  hiwDesc: { color: '#666', fontSize: '0.9rem', lineHeight: 1.6 },

  // ── Trust signals — dark, glass-morphism, enterprise ───────────────
  trustSection: {
    // Deep navy with a subtle gradient towards a slightly lighter tone
    // at the bottom — gives the section an "executive briefing" feel
    // and a hard visual break from the rest of the lighter page.
    background: 'linear-gradient(180deg, #0b1220 0%, #0f172a 60%, #111c34 100%)',
    color: '#e2e8f0',
    padding: 'clamp(56px, 9vw, 104px) 0 clamp(64px, 10vw, 112px)',
    position: 'relative',
    overflow: 'hidden',
  },
  trustGrid: {
    // Faint dot grid for texture. Masked out at the edges so the dots
    // never get close enough to compete with the cards.
    position: 'absolute', inset: 0,
    backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
    maskImage: 'radial-gradient(ellipse at center, #000 35%, transparent 75%)',
    WebkitMaskImage: 'radial-gradient(ellipse at center, #000 35%, transparent 75%)',
    pointerEvents: 'none',
  },
  trustGlow: (color) => ({
    position: 'absolute',
    width: 520, height: 520, borderRadius: '50%',
    background: `radial-gradient(circle, ${color}30 0%, transparent 65%)`,
    filter: 'blur(20px)',
    pointerEvents: 'none',
  }),
  trustEyebrow: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '6px 14px', borderRadius: 999,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#cbd5e1',
    fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.14em',
    textTransform: 'uppercase', marginBottom: 18,
    backdropFilter: 'blur(6px)',
  }),
  trustHeaderTitle: {
    fontSize: 'clamp(1.9rem, 3.4vw, 2.7rem)', fontWeight: 800,
    letterSpacing: '-0.025em', color: '#f8fafc',
    marginBottom: 14, lineHeight: 1.15,
    maxWidth: 820, marginLeft: 'auto', marginRight: 'auto',
  },
  trustHeaderSub: {
    color: '#94a3b8', fontSize: 'clamp(0.98rem, 1.4vw, 1.1rem)',
    maxWidth: 680, margin: '0 auto', lineHeight: 1.6,
  },
  trustCard: {
    padding: '28px 26px 24px',
    borderRadius: 18,
    // Glass effect: translucent fill + 1px border with stronger top
    // edge so cards feel "lit from above". Backdrop blur softens
    // anything underneath.
    background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 100%)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.30)',
    backdropFilter: 'blur(10px)',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform .28s ease, box-shadow .28s ease, border-color .28s ease, background .28s ease',
    display: 'flex', flexDirection: 'column',
  },
  trustCardHover: (color) => ({
    transform: 'translateY(-5px)',
    boxShadow: `0 1px 0 rgba(255,255,255,0.08) inset, 0 22px 50px rgba(0,0,0,0.45), 0 0 0 1px ${color}55`,
    borderColor: `${color}66`,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.035) 100%)',
  }),
  trustCardAccent: (color) => ({
    position: 'absolute', top: 0, left: 0, right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${color}, ${color}66 70%, transparent)`,
    transform: 'scaleX(0)', transformOrigin: 'left center',
    transition: 'transform .35s ease',
  }),
  trustCardAccentActive: { transform: 'scaleX(1)' },
  trustIcon: (color, accent) => ({
    width: 50, height: 50, borderRadius: 13,
    background: `linear-gradient(135deg, ${color} 0%, ${accent || color} 100%)`,
    color: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: `0 8px 18px ${color}45`,
  }),
  trustStatRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 16,
  },
  trustStat: (color) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
    color: '#fff',
  }),
  trustStatNumber: (color) => ({
    fontSize: '1.45rem', fontWeight: 800,
    lineHeight: 1, color: color,
    letterSpacing: '-0.02em',
    fontVariantNumeric: 'tabular-nums',
  }),
  trustStatLabel: {
    fontSize: '0.7rem', color: '#94a3b8',
    fontWeight: 600, letterSpacing: '0.04em',
    textTransform: 'uppercase', marginTop: 4,
  },
  trustTitle: {
    fontWeight: 700, fontSize: '1.1rem', marginBottom: 8, color: '#f8fafc',
    letterSpacing: '-0.01em',
  },
  trustDesc: {
    color: '#cbd5e1', fontSize: '0.9rem', lineHeight: 1.6, margin: 0,
    flex: 1,
  },
  trustHighlight: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    marginTop: 18, paddingTop: 14,
    borderTop: '1px solid rgba(255,255,255,0.08)',
    color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 600,
  }),
  trustHighlightDot: (color) => ({
    width: 6, height: 6, borderRadius: '50%', background: color,
    boxShadow: `0 0 10px ${color}`, flexShrink: 0,
  }),

  // ── Featured trust card (spans 2 cols on lg) ───────────────────────
  trustCardFeatured: (color, accent) => ({
    background: `linear-gradient(135deg, rgba(255,255,255,0.08) 0%, ${color}20 50%, ${accent || color}20 100%)`,
    border: `1px solid ${color}55`,
    boxShadow: `0 1px 0 rgba(255,255,255,0.06) inset, 0 18px 40px rgba(0,0,0,0.45), 0 0 0 1px ${accent || color}15`,
  }),
  trustFeaturedBadge: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 10px', borderRadius: 999,
    background: `${color}22`, color,
    fontSize: '0.66rem', fontWeight: 800,
    letterSpacing: '0.14em', textTransform: 'uppercase',
    border: `1px solid ${color}44`,
    alignSelf: 'flex-start',
    marginBottom: 12,
  }),
  trustFeaturedTitle: {
    fontWeight: 800, fontSize: '1.4rem', color: '#f8fafc',
    letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 12,
  },
  trustFeaturedDesc: {
    color: '#cbd5e1', fontSize: '0.98rem', lineHeight: 1.65,
    marginBottom: 18,
  },
  trustQuoteBox: {
    marginTop: 18, paddingTop: 18,
    borderTop: '1px solid rgba(255,255,255,0.1)',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  trustQuoteText: {
    color: '#e2e8f0', fontSize: '0.95rem', lineHeight: 1.55,
    fontStyle: 'italic', position: 'relative',
    paddingLeft: 20,
  },
  trustQuoteMark: (color) => ({
    position: 'absolute', left: 0, top: -4,
    fontSize: '1.8rem', lineHeight: 1, color,
    fontWeight: 800,
  }),
  trustQuoteAuthor: {
    color: '#94a3b8', fontSize: '0.82rem', fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 8,
  },
  trustQuoteAuthorAvatar: (color) => ({
    width: 26, height: 26, borderRadius: '50%',
    background: `linear-gradient(135deg, ${color}, ${color}99)`,
    color: '#fff', fontWeight: 800, fontSize: '0.72rem',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  }),

  // ── Dark customer-logo strip below stats band ──────────────────────
  trustLogoStrip: {
    marginTop: 32, paddingTop: 32,
    borderTop: '1px solid rgba(255,255,255,0.08)',
    textAlign: 'center',
  },
  trustLogoLabel: {
    color: '#64748b', fontSize: '0.72rem',
    fontWeight: 700, letterSpacing: '0.18em',
    textTransform: 'uppercase', marginBottom: 18,
  },
  trustLogoRow: {
    display: 'flex', flexWrap: 'wrap',
    alignItems: 'center', justifyContent: 'center',
    gap: 36,
  },
  trustLogoText: {
    color: '#64748b', fontWeight: 700, fontSize: '0.95rem',
    letterSpacing: '0.02em', opacity: 0.78,
    transition: 'color .2s, opacity .2s',
  },
  trustLogoImg: {
    height: 28, maxWidth: 130, objectFit: 'contain',
    filter: 'grayscale(1) brightness(1.6)', opacity: 0.55,
    transition: 'opacity .2s, filter .2s',
  },

  // ── Enterprise stat band (sits below the cards) ────────────────────
  trustStatsBand: {
    marginTop: 56,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 18,
    padding: '24px 8px',
    backdropFilter: 'blur(6px)',
    display: 'flex', flexWrap: 'wrap',
  },
  trustBandItem: {
    flex: '1 1 180px',
    padding: '10px 18px',
    textAlign: 'center',
    borderLeft: '1px solid rgba(255,255,255,0.07)',
  },
  trustBandValue: (color) => ({
    fontSize: 'clamp(1.6rem, 2.4vw, 2rem)',
    fontWeight: 800, color: '#fff',
    letterSpacing: '-0.02em', lineHeight: 1.05,
    fontVariantNumeric: 'tabular-nums',
  }),
  trustBandLabel: {
    fontSize: '0.78rem', color: '#94a3b8',
    fontWeight: 600, letterSpacing: '0.06em',
    textTransform: 'uppercase', marginTop: 6,
  },

  footer: { background: '#1a1a2e', color: '#ccc', padding: '48px 0 24px', fontSize: '0.9rem' },
  footerTitle: { fontWeight: 700, color: '#fff', fontSize: '1rem', marginBottom: 16 },
  footerLink: {
    color: '#aaa', textDecoration: 'none', display: 'block', marginBottom: 8,
    fontSize: '0.88rem', transition: 'color .2s',
  },
  footerBottom: {
    borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 32,
    paddingTop: 20, textAlign: 'center', color: '#777', fontSize: '0.82rem',
  },

  filterPanel: {
    background: '#fff', borderRadius: 14, border: '1px solid #eee',
    padding: 24, marginBottom: 24, boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
  },
  resultsHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20, flexWrap: 'wrap', gap: 12,
  },

  // ── Customer logo strip ────────────────────────────────────────────
  logoStrip: {
    background: '#faf7ff',
    padding: '40px 0 36px',
    borderBottom: '1px solid #f1f5f9',
  },
  logoStripLabel: {
    textAlign: 'center',
    color: '#64748b', fontSize: '0.74rem',
    fontWeight: 700, letterSpacing: '0.18em',
    textTransform: 'uppercase',
    marginBottom: 22,
  },
  logoStripRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 48, flexWrap: 'wrap',
  },
  logoStripItem: {
    height: 36,
    display: 'inline-flex', alignItems: 'center',
    color: '#94a3b8', fontWeight: 700, fontSize: '1.05rem',
    letterSpacing: '0.02em',
    opacity: 0.85,
    transition: 'opacity .2s, color .2s',
    filter: 'grayscale(1)',
  },
  logoStripImg: {
    height: 32, maxWidth: 140, objectFit: 'contain',
    filter: 'grayscale(1)', opacity: 0.7,
    transition: 'opacity .2s, filter .2s',
  },

  // ── Industry use cases ─────────────────────────────────────────────
  industrySection: { background: '#faf7ff', padding: 'clamp(48px, 8vw, 88px) 0' },
  industryCard: {
    background: '#ffffff', borderRadius: 16,
    border: '1px solid #ede9fe', height: '100%',
    padding: 26, display: 'flex', flexDirection: 'column',
    transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  industryCardHover: {
    transform: 'translateY(-4px)',
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
  },
  industryIcon: (color) => ({
    width: 44, height: 44, borderRadius: 12,
    background: `${color}15`, color,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: 14,
  }),
  industryTitle: { fontWeight: 700, fontSize: '1.08rem', color: '#0f172a', marginBottom: 8, letterSpacing: '-0.01em' },
  industryDesc: { color: '#475569', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 14 },
  industryPoint: {
    display: 'flex', alignItems: 'center', gap: 8,
    color: '#334155', fontSize: '0.85rem',
    padding: '4px 0',
  },
  industryCTA: (color) => ({
    marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9',
    color, fontWeight: 700, fontSize: '0.86rem',
    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
  }),

  // ── Host CTA section ───────────────────────────────────────────────
  hostCTASection: (gradStart, gradEnd) => ({
    padding: 'clamp(44px, 7vw, 80px) 0',
    background: `linear-gradient(135deg, ${gradStart}05 0%, ${gradEnd}10 100%)`,
    position: 'relative',
    overflow: 'hidden',
  }),
  hostCTACard: {
    background: '#ffffff',
    borderRadius: 24,
    // Fluid padding so the card never crowds the edges on small
    // viewports. Older devices that don't support clamp fall back to
    // the second value.
    padding: 'clamp(28px, 5vw, 48px) clamp(20px, 5vw, 56px)',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
    border: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column', gap: 28,
  },
  hostCTAEyebrow: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '5px 12px', borderRadius: 999,
    background: `${color}15`, color,
    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em',
    textTransform: 'uppercase', alignSelf: 'flex-start',
  }),
  hostCTATitle: {
    fontSize: 'clamp(1.5rem, 2.6vw, 2rem)', fontWeight: 800,
    letterSpacing: '-0.02em', color: '#0f172a', lineHeight: 1.2,
  },
  hostCTASub: {
    color: '#475569', fontSize: 'clamp(0.95rem, 1.3vw, 1.05rem)',
    lineHeight: 1.6, marginBottom: 8, maxWidth: 560,
  },
  hostBenefit: {
    display: 'flex', alignItems: 'flex-start', gap: 14,
    padding: '16px 0',
    borderTop: '1px solid #f1f5f9',
  },
  hostBenefitIcon: (color) => ({
    width: 40, height: 40, borderRadius: 10,
    background: `${color}15`, color,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  }),
  hostBenefitTitle: { fontWeight: 700, color: '#0f172a', marginBottom: 3, fontSize: '0.95rem' },
  hostBenefitDesc: { color: '#475569', fontSize: '0.86rem', lineHeight: 1.5 },
  hostCTAButton: (color, accent) => ({
    background: `linear-gradient(135deg, ${color} 0%, ${accent || color} 100%)`,
    color: '#fff', border: 'none', borderRadius: 12,
    padding: '14px 32px', fontWeight: 700, fontSize: '0.95rem',
    cursor: 'pointer', textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    boxShadow: `0 8px 22px ${color}40`,
    transition: 'transform .15s, box-shadow .15s',
    alignSelf: 'flex-start',
  }),

  // ── Final CTA banner ───────────────────────────────────────────────
  finalCTA: (gradStart, gradEnd) => ({
    background: `linear-gradient(135deg, ${gradStart} 0%, ${gradEnd} 100%)`,
    padding: 'clamp(40px, 7vw, 72px) 0', color: '#fff',
    position: 'relative', overflow: 'hidden',
    textAlign: 'center',
  }),
  finalCTATitle: {
    fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)', fontWeight: 800,
    letterSpacing: '-0.02em', lineHeight: 1.2,
    marginBottom: 12,
    maxWidth: 760, marginLeft: 'auto', marginRight: 'auto',
  },
  finalCTASub: {
    fontSize: 'clamp(0.98rem, 1.4vw, 1.12rem)', opacity: 0.92,
    marginBottom: 28,
    maxWidth: 600, marginLeft: 'auto', marginRight: 'auto',
    lineHeight: 1.55,
  },
  finalCTAButton: {
    background: '#fff', color: '#0f172a',
    border: 'none', borderRadius: 12,
    padding: '14px 32px', fontWeight: 700, fontSize: '1rem',
    cursor: 'pointer', textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.18)',
    transition: 'transform .15s, box-shadow .15s',
  },
  finalCTASecondary: {
    background: 'transparent', color: '#fff',
    border: '1.5px solid rgba(255, 255, 255, 0.5)',
    borderRadius: 12,
    padding: '12.5px 28px', fontWeight: 600, fontSize: '0.95rem',
    cursor: 'pointer', textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    marginLeft: 12,
    transition: 'background .15s',
  },
};
